import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import {
  getRegistrationById,
  listExtraActivities,
  listFrequencies,
  updateStudent,
  uploadStudentPhotoEdit,
} from "../services/student";
import { levelKey } from "../utils/classesLabels";
import { getSucursalInfo, getUserInfo, hasSpecialHourSchedule } from "../services/auth";
import { listPaymentTypes } from "../services/paymentTypes";
import {
  DISCOUNT_OPTIONS,
  applyDiscount,
  buildDiscountPayloadFields,
  computeBaseTotals,
} from "../utils/studentPricing";

const currentYear = new Date().getFullYear();
const money = (v) => (parseFloat(v) || 0).toFixed(2);

// birthDate as persisted can be a plain "YYYY-MM-DD"/ISO string, a Firestore
// Timestamp ({ seconds, nanoseconds } or with a toDate() method), a Date
// instance, or an epoch value — normalize all of them to the "YYYY-MM-DD"
// shape the <input type="date"> control expects.
const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.split("T")[0];

  let date = null;
  if (typeof value?.toDate === "function") {
    date = value.toDate();
  } else if (typeof value?.seconds === "number") {
    date = new Date(value.seconds * 1000);
  } else if (value instanceof Date) {
    date = value;
  } else if (typeof value === "number") {
    date = new Date(value);
  }

  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

// Mirrors sge_infantil_web/src/pages/student/edit/index.js componentWillMount:
// registration data + its nested student/carier are flattened into one flat
// edit form, including reconstructing the discount UI state from whatever
// was persisted on the registration (percentages stored as MZN amounts on
// "both" mode, percentage vs numeric discountType, etc).
const fieldsFromRegistration = (record) => {
  const student = record?.student || {};
  const carier = student.carier || {};

  const storedScholarshipType = record?.scholarshipType || "none";
  const storedIsDiscountNumeric = record?.isDiscountNumeric || false;
  const storedApplyToPayment = record?.applyToPayment || false;
  const storedApplyToRegistration = record?.applyToRegistration || false;
  const storedRegistrationDiscount = parseFloat(record?.registrationDiscount) || 0;
  const storedMonthlyDiscount = parseFloat(record?.monthlyDiscount) || 0;
  const origReg = parseFloat(record?.originalRegistrationValue) || 0;
  const origMonthly = parseFloat(record?.originalMonthlyPayment) || 0;

  const hasDiscount =
    storedScholarshipType !== "none" ||
    storedRegistrationDiscount > 0 ||
    storedMonthlyDiscount > 0 ||
    record?.includeDiscount === true;

  let discountAppliesTo = "registration";
  if (storedApplyToPayment && storedApplyToRegistration) discountAppliesTo = "both";
  else if (storedApplyToPayment) discountAppliesTo = "payment";
  else if (storedApplyToRegistration) discountAppliesTo = "registration";

  let registrationDiscount;
  let monthlyDiscount;
  let discount;
  if (!storedIsDiscountNumeric) {
    registrationDiscount = origReg > 0 ? storedRegistrationDiscount / origReg : 0;
    monthlyDiscount = origMonthly > 0 ? storedMonthlyDiscount / origMonthly : 0;
    discount = (parseFloat(record?.discount) || 0) / 100;
  } else {
    registrationDiscount = storedRegistrationDiscount;
    monthlyDiscount = storedMonthlyDiscount;
    discount = parseFloat(record?.discount) || 0;
  }

  const studentImage = student.imageUrl || student.imageURL || student.picture || "";

  return {
    studentId: student.id,
    registrationId: record?.id,
    pictureFilename: studentImage,
    imagePreview: studentImage,
    uploadingPhoto: false,

    name: student.name || "",
    code: student.code || "",
    birthDate: toDateInputValue(student.birthDate),
    gender: student.sex || "",
    docType: student.docType || "",
    docNumber: student.docNumber || "",
    studentAddress: student.address || student.studentAddress || "",
    hasDesease: student.sufferFromAnyIllness ? "1" : "0",
    deseaseDesc: student.sufferFromAnyIllness || "",
    isNew: record?.isNew ?? false,
    enrollmentYear: record?.enrollmentYear || currentYear,
    isAlergicToFood: !!student.alergicToFood,
    alergicToFood: student.alergicToFood || "",
    isAlergicToMedicine: !!student.alergicToMedicine,
    alergicToMedicine: student.alergicToMedicine || "",

    fatherName: student.fatherName || "",
    motherName: student.motherName || "",
    fatherContact: student.fatherContact || "",
    motherContact: student.motherContact || "",

    kinshipDegree: carier.kinshipDegree || "",
    carierName: carier.name || "",
    jobLocation: carier.workPlace || "",
    carierContact: carier.contact || "",
    carierSecondContact: carier.secondContact || "",
    address: student.address || "",

    selectedYear: record?.year || currentYear,
    frequency: record?.level || record?.corseId || student.level || "",
    extraCuricularActivities: (record?.extraconfigs || []).map((ea) => String(ea.id)),
    paymentMethod: record?.paymentMethod || "POS",
    scheduleType: record?.scheduleType || "7h-12h",

    scholarshipType: storedScholarshipType,
    discountType: storedIsDiscountNumeric ? "numeric" : "percentage",
    discountAppliesTo,
    discount,
    registrationDiscount,
    monthlyDiscount,
    exemptRegistration: record?.exemptRegistration || false,
    includeDiscount: hasDiscount,

    originalRegistrationValue: origReg,
    originalMonthlyPayment: origMonthly,
    registrationValue: parseFloat(record?.totalPaid) || 0,
    monthlyPayment: parseFloat(record?.monthlyPayment) || 0,
    total: parseFloat(record?.totalPaid) || 0,
  };
};

const EditarEstudanteLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [registration, setRegistration] = useState(location.state?.registration || null);
  const [loading, setLoading] = useState(!location.state?.registration);
  const [saving, setSaving] = useState(false);
  const [frequencies, setFrequencies] = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [form, setForm] = useState(fieldsFromRegistration(location.state?.registration));

  // Always refetch from the server, even when the list screen handed us a
  // `location.state.registration` snapshot to render instantly: that state
  // is backed by the browser's history.state, so it survives a full page
  // reload (F5) and would otherwise keep showing pre-save data — e.g. an
  // alternate guardian contact just saved would appear to have vanished on
  // reload, even though it was persisted correctly. The snapshot is only
  // used to avoid a loading flash; the fetch below is what's authoritative.
  useEffect(() => {
    getRegistrationById(id)
      .then((resp) => {
        setRegistration(resp.data || null);
        if (resp.data) setForm(fieldsFromRegistration(resp.data));
      })
      .catch(() => {
        if (!registration) setRegistration(null);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    listFrequencies()
      .then((resp) => setFrequencies(resp.data || []))
      .catch(() => setFrequencies([]));
    listExtraActivities()
      .then((resp) => setAllActivities(resp.data || []))
      .catch(() => setAllActivities([]));
    // Tipos de Pagamento por sucursal (SGE_PAYMENT_TYPES) - ver services/paymentTypes.
    listPaymentTypes()
      .then((resp) => setPaymentMethods(resp.data || []))
      .catch(() => setPaymentMethods([]));
  }, []);

  const setField = (field) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const setChecked = (field) => (e) => {
    const checked = e.target.checked;
    setForm((f) => ({ ...f, [field]: checked }));
  };

  const selectedFrequency = useMemo(
    () => frequencies.find((f) => String(f.id) === String(form.frequency)),
    [frequencies, form.frequency],
  );

  const activitiesForFrequency = useMemo(
    () => allActivities.filter((a) => String(a.levelId) === String(selectedFrequency?.id)),
    [allActivities, selectedFrequency],
  );

  const handleScholarshipType = (e) => {
    const value = e.target.value;
    setForm((f) => ({
      ...f,
      scholarshipType: value,
      discount: 0,
      registrationDiscount: 0,
      monthlyDiscount: 0,
      exemptRegistration: false,
    }));
  };

  const handleSelectKinship = (e) => {
    const kinshipDegree = e.target.value;
    setForm((f) => {
      if (kinshipDegree === "PAI") {
        return { ...f, kinshipDegree, carierName: f.fatherName, carierContact: f.fatherContact };
      }
      if (kinshipDegree === "MAE") {
        return { ...f, kinshipDegree, carierName: f.motherName, carierContact: f.motherContact };
      }
      return { ...f, kinshipDegree };
    });
  };

  const handleActivitiesChange = (e) => {
    const values = Array.from(e.target.selectedOptions).map((opt) => opt.value);
    setForm((f) => ({ ...f, extraCuricularActivities: values }));
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, imagePreview: reader.result }));
    reader.readAsDataURL(file);

    setForm((f) => ({ ...f, uploadingPhoto: true }));
    try {
      const resp = await uploadStudentPhotoEdit(file);
      setForm((f) => ({ ...f, pictureFilename: resp.data?.file || "", uploadingPhoto: false }));
    } catch (err) {
      setForm((f) => ({ ...f, uploadingPhoto: false }));
      toast.error(t("estudante.error.photo.upload"));
    }
  };

  // Recompute totals only when the operator actually touches the class,
  // activities, or discount configuration — otherwise keep whatever was
  // already persisted on the registration (see fieldsFromRegistration).
  const recalculate = () => {
    const selectedActs = activitiesForFrequency.filter((a) =>
      form.extraCuricularActivities.includes(String(a.id)),
    );
    const base = computeBaseTotals({
      freq: selectedFrequency,
      isNew: form.isNew,
      selectedActivities: selectedActs,
    });
    const originalRegistrationValue = base.registrationValue;
    const originalMonthlyPayment = base.monthlyPayment;

    let next;
    if (form.scholarshipType === "none") {
      next = {
        originalRegistrationValue,
        originalMonthlyPayment,
        registrationValue: base.registrationValue,
        monthlyPayment: base.monthlyPayment,
        total: base.total,
      };
    } else {
      const discounted = applyDiscount({
        scholarshipType: form.scholarshipType,
        discountType: form.discountType,
        discountAppliesTo: form.discountAppliesTo,
        discount: form.discount,
        registrationDiscount: form.registrationDiscount,
        monthlyDiscount: form.monthlyDiscount,
        exemptRegistration: form.exemptRegistration,
        originalRegistrationValue,
        originalMonthlyPayment,
      });
      next = { originalRegistrationValue, originalMonthlyPayment, ...discounted };
    }
    setForm((f) => ({ ...f, ...next, includeDiscount: form.scholarshipType !== "none" }));
  };

  const handleSave = async () => {
    if (
      !form.name ||
      !form.studentAddress ||
      !form.fatherName ||
      !form.motherName ||
      !form.carierName ||
      !form.address ||
      !form.kinshipDegree ||
      !form.frequency ||
      !form.selectedYear ||
      !form.gender ||
      !form.docType
    ) {
      toast.error(t("validation.required"));
      return;
    }
    if (form.carierContact && String(form.carierContact).length !== 9) {
      toast.error(t("estudante.validation.contact.length"));
      return;
    }
    if (form.carierSecondContact && String(form.carierSecondContact).length !== 9) {
      toast.error(t("estudante.validation.contact.length"));
      return;
    }

    setSaving(true);
    const userId = getUserInfo()?.id;
    const sucursal = getSucursalInfo();

    const extraActivities = activitiesForFrequency
      .filter((ea) => form.extraCuricularActivities.includes(String(ea.id)))
      .map((ea) => ({ id: ea.id, name: ea.name, recurringAmount: ea.recuringAmount }));

    const freqRegistrationValue = form.isNew
      ? parseFloat(selectedFrequency?.registrationValue) || 0
      : parseFloat(selectedFrequency?.recurigRegistrationValue) || 0;
    const freqMonthlyPayment = parseFloat(selectedFrequency?.monthlyPayment) || 0;

    const { discountToSend, registrationDiscountToSend, monthlyDiscountToSend } =
      buildDiscountPayloadFields({
        discountType: form.discountType,
        discount: form.discount,
        registrationDiscount: form.registrationDiscount,
        monthlyDiscount: form.monthlyDiscount,
        freqRegistrationValue,
        freqMonthlyPayment,
      });

    const discountData =
      form.scholarshipType !== "none"
        ? {
            includeDiscount: true,
            discountType: form.discountType,
            discountAppliesTo: form.discountAppliesTo,
            discount: discountToSend,
            scholarshipType: form.scholarshipType,
            registrationDiscount: registrationDiscountToSend,
            monthlyDiscount: monthlyDiscountToSend,
            exemptRegistration: form.exemptRegistration,
            originalRegistrationValue: form.originalRegistrationValue,
            originalMonthlyPayment: form.originalMonthlyPayment,
            registrationValue: form.registrationValue,
          }
        : { includeDiscount: false };

    try {
      await updateStudent(form.studentId, {
        name: form.name,
        code: form.code,
        address: form.address,
        sex: form.gender,
        carierName: form.carierName,
        kinshipDegree: form.kinshipDegree,
        workPlace: form.jobLocation,
        sucursal,
        imageUrl: form.pictureFilename,
        contact: form.carierContact,
        secondContact: form.carierSecondContact,
        birthDate: form.birthDate,
        docType: form.docType,
        currentMonthlyPayment: form.monthlyPayment,
        studentAddress: form.studentAddress,
        corseId: selectedFrequency?.id,
        level: form.frequency,
        docNumber: form.docNumber,
        alergicToFood: form.alergicToFood || false,
        alergicToMedicine: form.alergicToMedicine || false,
        motherContact: form.motherContact,
        fatherContact: form.fatherContact,
        motherName: form.motherName,
        fatherName: form.fatherName,
        sucursalId: sucursal?.id,
        updatedBy: userId,
        sufferFromAnyIllness: form.deseaseDesc || "",
        extraconfigs: extraActivities,
        registrationId: form.registrationId,
        mobile: form.carierContact,
        paymentMethod: form.paymentMethod || "POS",
        ...discountData,
      });
      toast.success(t("estudante.edit.success"));
      navigate("/estudantes/activos");
    } catch (err) {
      toast.error(t("global.error.message"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='card'>
        <div className='card-body text-secondary-light'>{t("estudante.list.loading")}</div>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className='card'>
        <div className='card-body text-center py-64'>
          <p className='text-secondary-light'>{t("estudante.edit.not.found")}</p>
          <button
            type='button'
            className='btn btn-primary-600 px-32'
            onClick={() => navigate("/estudantes/activos")}
          >
            <i className='ri-arrow-left-line' /> {t("estudante.edit.back.to.list")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-lg-4'>
        <div className='user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100'>
          <img
            src='assets/images/user-grid/user-grid-bg1.png'
            alt=''
            className='w-100 object-fit-cover'
          />
          <div className='pb-24 ms-16 mb-24 me-16 mt--100'>
            <div className='text-center border border-top-0 border-start-0 border-end-0'>
              <div className='w-200-px h-200-px mx-auto border br-white border-width-2-px rounded-circle overflow-hidden bg-neutral-100 d-flex align-items-center justify-content-center'>
                {form.imagePreview ? (
                  <img
                    src={form.imagePreview}
                    alt=''
                    className='w-100 h-100 object-fit-cover'
                  />
                ) : (
                  <i className='ri-user-3-line text-4xl text-neutral-400' />
                )}
              </div>
              <h6 className='mb-0 mt-16'>{form.name}</h6>
              <span className='text-secondary-light mb-16'>{form.code || "-"}</span>
              <div className='mt-12'>
                <input
                  type='file'
                  accept='image/png,image/jpeg'
                  className='form-control form-control-sm'
                  onChange={handlePhotoChange}
                />
              </div>
            </div>
            <div className='mt-24'>
              <h6 className='text-xl mb-16'>{t("global.personal.information")}</h6>
              <ul>
                <li className='d-flex align-items-center gap-1 mb-12'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("estudante.field.docType")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {form.docType} {form.docNumber}
                  </span>
                </li>
                <li className='d-flex align-items-center gap-1 mb-12'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t(levelKey("estudante.field.frequency"))}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {selectedFrequency?.description || "-"}
                  </span>
                </li>
                <li className='d-flex align-items-center gap-1 mb-12'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("estudante.field.carierContact")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {form.carierContact || "-"}
                  </span>
                </li>
                {form.carierSecondContact && (
                  <li className='d-flex align-items-center gap-1 mb-12'>
                    <span className='w-50 text-md fw-semibold text-primary-light'>
                      {t("estudante.field.secondCarierContact")}
                    </span>
                    <span className='w-50 text-secondary-light fw-medium'>
                      : {form.carierSecondContact}
                    </span>
                  </li>
                )}
                <li className='d-flex align-items-center gap-1'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("estudante.summary.total")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>: {money(form.total)}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className='col-lg-8'>
        <div className='card h-100'>
          <div className='card-body p-24'>
            <h6 className='text-md text-primary-light mb-16'>{t("estudante.edit.title")}</h6>
            <p className='text-secondary-light text-sm mb-24'>{t("estudante.edit.subtitle")}</p>

            <ul
              className='nav border-gradient-tab nav-pills mb-20 d-inline-flex'
              id='estudante-edit-tabs'
              role='tablist'
            >
              <li className='nav-item' role='presentation'>
                <button
                  className='nav-link d-flex align-items-center px-24 active'
                  id='tab-dados-pessoais-tab'
                  data-bs-toggle='pill'
                  data-bs-target='#tab-dados-pessoais'
                  type='button'
                  role='tab'
                  aria-controls='tab-dados-pessoais'
                  aria-selected='true'
                >
                  {t("estudante.wizard.step.personal")}
                </button>
              </li>
              <li className='nav-item' role='presentation'>
                <button
                  className='nav-link d-flex align-items-center px-24'
                  id='tab-encarregado-tab'
                  data-bs-toggle='pill'
                  data-bs-target='#tab-encarregado'
                  type='button'
                  role='tab'
                  aria-controls='tab-encarregado'
                  aria-selected='false'
                  tabIndex={-1}
                >
                  {t("estudante.wizard.step.guardian")}
                </button>
              </li>
              <li className='nav-item' role='presentation'>
                <button
                  className='nav-link d-flex align-items-center px-24'
                  id='tab-inscricao-tab'
                  data-bs-toggle='pill'
                  data-bs-target='#tab-inscricao'
                  type='button'
                  role='tab'
                  aria-controls='tab-inscricao'
                  aria-selected='false'
                  tabIndex={-1}
                >
                  {t("estudante.wizard.step.enrollment")}
                </button>
              </li>
              <li className='nav-item' role='presentation'>
                <button
                  className='nav-link d-flex align-items-center px-24'
                  id='tab-documentos-tab'
                  data-bs-toggle='pill'
                  data-bs-target='#tab-documentos'
                  type='button'
                  role='tab'
                  aria-controls='tab-documentos'
                  aria-selected='false'
                  tabIndex={-1}
                >
                  {t("estudante.edit.tab.documents")}
                </button>
              </li>
            </ul>

            <div className='tab-content' id='estudante-edit-tabContent'>
            <div
              className='tab-pane fade show active'
              id='tab-dados-pessoais'
              role='tabpanel'
              aria-labelledby='tab-dados-pessoais-tab'
            >
            <div className='row'>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.name")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="f7:person" />
                    </span>
                    <input
                      type='text'
                      className='form-control radius-8'
                      value={form.name}
                      onChange={setField("name")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-3'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.code")}
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:hashtag-square-outline" />
                    </span>
                    <input
                      type='text'
                      className='form-control radius-8'
                      value={form.code}
                      onChange={setField("code")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-3'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("global.birth.date")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:calendar-outline" />
                    </span>
                    <input
                      type='date'
                      className='form-control radius-8'
                      value={form.birthDate}
                      onChange={setField("birthDate")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("global.gender")} <span className='text-danger-600'>*</span>
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.gender}
                    onChange={setField("gender")}
                  >
                    <option value=''>{t("estudante.select.placeholder")}</option>
                    <option value='M'>{t("global.male")}</option>
                    <option value='F'>{t("global.female")}</option>
                  </select>
                </div>
              </div>
              <div className='col-sm-12'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.studentAddress")} <span className='text-danger-600'>*</span>
                  </label>
                  <textarea
                    rows={2}
                    className='form-control radius-8'
                    value={form.studentAddress}
                    onChange={setField("studentAddress")}
                  />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.hasDesease")}
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.hasDesease}
                    onChange={setField("hasDesease")}
                  >
                    <option value='0'>{t("global.no")}</option>
                    <option value='1'>{t("global.yes")}</option>
                  </select>
                </div>
              </div>
              {form.hasDesease === "1" && (
                <div className='col-sm-8'>
                  <div className='mb-20'>
                    <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                      {t("estudante.field.deseaseDesc")}
                    </label>
                    <div className="icon-field">
                      <span className="icon">
                        <Icon icon="solar:pen-2-outline" />
                      </span>
                      <input
                        type='text'
                        className='form-control radius-8'
                        value={form.deseaseDesc}
                        onChange={setField("deseaseDesc")}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <hr className='my-16' />
            <h6 className='text-sm text-uppercase text-secondary-light mb-16'>
              {t("estudante.wizard.step.filiation")}
            </h6>
            <div className='row'>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.fatherName")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="f7:person" />
                    </span>
                    <input
                      type='text'
                      className='form-control radius-8'
                      value={form.fatherName}
                      onChange={setField("fatherName")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.motherName")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="f7:person" />
                    </span>
                    <input
                      type='text'
                      className='form-control radius-8'
                      value={form.motherName}
                      onChange={setField("motherName")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.fatherContact")}
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:phone-calling-linear" />
                    </span>
                    <input
                      type='text'
                      maxLength={9}
                      className='form-control radius-8'
                      value={form.fatherContact}
                      onChange={setField("fatherContact")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.motherContact")}
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:phone-calling-linear" />
                    </span>
                    <input
                      type='text'
                      maxLength={9}
                      className='form-control radius-8'
                      value={form.motherContact}
                      onChange={setField("motherContact")}
                    />
                  </div>
                </div>
              </div>
            </div>
            </div>

            <div
              className='tab-pane fade'
              id='tab-encarregado'
              role='tabpanel'
              aria-labelledby='tab-encarregado-tab'
            >
            <div className='row'>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.kinshipDegree")} <span className='text-danger-600'>*</span>
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.kinshipDegree}
                    onChange={handleSelectKinship}
                  >
                    <option value=''>{t("estudante.select.placeholder")}</option>
                    <option value='PAI'>{t("estudante.kinship.father")}</option>
                    <option value='MAE'>{t("estudante.kinship.mother")}</option>
                    <option value='TIO'>{t("estudante.kinship.uncle")}</option>
                    <option value='AVO'>{t("estudante.kinship.grandparent")}</option>
                    <option value='IRMAO'>{t("estudante.kinship.sibling")}</option>
                  </select>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.carierName")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="f7:person" />
                    </span>
                    <input
                      type='text'
                      className='form-control radius-8'
                      value={form.carierName}
                      onChange={setField("carierName")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.jobLocation")}
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:map-point-outline" />
                    </span>
                    <input
                      type='text'
                      className='form-control radius-8'
                      value={form.jobLocation}
                      onChange={setField("jobLocation")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.carierContact")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:phone-calling-linear" />
                    </span>
                    <input
                      type='text'
                      maxLength={9}
                      className='form-control radius-8'
                      value={form.carierContact}
                      onChange={setField("carierContact")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.secondCarierContact")}
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:phone-calling-linear" />
                    </span>
                    <input
                      type='text'
                      maxLength={9}
                      className='form-control radius-8'
                      value={form.carierSecondContact}
                      onChange={setField("carierSecondContact")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-12'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.address")} <span className='text-danger-600'>*</span>
                  </label>
                  <textarea
                    rows={2}
                    className='form-control radius-8'
                    value={form.address}
                    onChange={setField("address")}
                  />
                </div>
              </div>
            </div>
            </div>

            <div
              className='tab-pane fade'
              id='tab-inscricao'
              role='tabpanel'
              aria-labelledby='tab-inscricao-tab'
            >
            <div className='row'>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.selectedYear")} <span className='text-danger-600'>*</span>
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.selectedYear}
                    onChange={setField("selectedYear")}
                  >
                    {Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i).map(
                      (y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t(levelKey("estudante.field.frequency"))} <span className='text-danger-600'>*</span>
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.frequency}
                    onChange={(e) => {
                      setField("frequency")(e);
                    }}
                  >
                    <option value=''>{t("estudante.select.placeholder")}</option>
                    {frequencies.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.paymentMethod")}
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.paymentMethod}
                    onChange={setField("paymentMethod")}
                  >
                    {paymentMethods.map((m) => (
                      <option key={m.id} value={m.name}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {hasSpecialHourSchedule() && (
                <div className='col-sm-4'>
                  <div className='mb-20'>
                    <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                      {t("estudante.field.scheduleType")}
                    </label>
                    <select
                      className='form-control radius-8 form-select'
                      value={form.scheduleType}
                      onChange={setField("scheduleType")}
                    >
                      <option value='7h-12h'>{t("estudante.field.scheduleType.standard")}</option>
                      <option value='7h-15h'>{t("estudante.field.scheduleType.special")}</option>
                    </select>
                  </div>
                </div>
              )}
              <div className='col-sm-12'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.extraCuricularActivities")}
                  </label>
                  <select
                    multiple
                    className='form-control radius-8 form-select'
                    style={{ height: 90 }}
                    value={form.extraCuricularActivities}
                    onChange={handleActivitiesChange}
                  >
                    {activitiesForFrequency.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.scholarship.type")}
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.scholarshipType}
                    onChange={handleScholarshipType}
                  >
                    <option value='none'>{t("estudante.scholarship.type.none")}</option>
                    <option value='discount'>{t("estudante.scholarship.type.discount")}</option>
                    <option value='partial'>{t("estudante.scholarship.type.partial")}</option>
                    <option value='full'>{t("estudante.scholarship.type.full")}</option>
                  </select>
                </div>
              </div>

              {form.scholarshipType === "full" && (
                <>
                  <div className='col-sm-4 d-flex align-items-end'>
                    <div className='form-check mb-20'>
                      <input
                        id='exemptRegistrationEdit'
                        type='checkbox'
                        className='form-check-input'
                        checked={form.exemptRegistration}
                        onChange={setChecked("exemptRegistration")}
                      />
                      <label htmlFor='exemptRegistrationEdit' className='form-check-label'>
                        {t("estudante.scholarship.exemptRegistration")}
                      </label>
                    </div>
                  </div>
                  {!form.exemptRegistration && (
                    <div className='col-sm-4'>
                      <div className='mb-20'>
                        <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                          {t("estudante.scholarship.registrationDiscount")}
                        </label>
                        <select
                          className='form-control radius-8 form-select'
                          value={form.registrationDiscount}
                          onChange={setField("registrationDiscount")}
                        >
                          {DISCOUNT_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {Math.round(opt * 100)}%
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </>
              )}

              {(form.scholarshipType === "partial" || form.scholarshipType === "discount") && (
                <>
                  <div className='col-sm-4'>
                    <div className='mb-20'>
                      <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                        {t("estudante.discount.type")}
                      </label>
                      <select
                        className='form-control radius-8 form-select'
                        value={form.discountType}
                        onChange={setField("discountType")}
                      >
                        <option value='percentage'>{t("estudante.discount.type.percentage")}</option>
                        <option value='numeric'>{t("estudante.discount.type.numeric")}</option>
                      </select>
                    </div>
                  </div>
                  <div className='col-sm-4'>
                    <div className='mb-20'>
                      <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                        {t("estudante.discount.appliesTo")}
                      </label>
                      <select
                        className='form-control radius-8 form-select'
                        value={form.discountAppliesTo}
                        onChange={setField("discountAppliesTo")}
                      >
                        <option value='registration'>
                          {t("estudante.discount.appliesTo.registration")}
                        </option>
                        <option value='payment'>{t("estudante.discount.appliesTo.payment")}</option>
                        <option value='both'>{t("estudante.discount.appliesTo.both")}</option>
                      </select>
                    </div>
                  </div>
                  {form.discountAppliesTo === "both" ? (
                    <>
                      <div className='col-sm-4'>
                        <div className='mb-20'>
                          <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                            {t("estudante.discount.registration")}
                          </label>
                          <div className="icon-field">
                            <span className="icon">
                              <Icon icon="solar:ticket-sale-outline" />
                            </span>
                            <input
                              type='number'
                              className='form-control radius-8'
                              value={form.registrationDiscount}
                              onChange={setField("registrationDiscount")}
                            />
                          </div>
                        </div>
                      </div>
                      <div className='col-sm-4'>
                        <div className='mb-20'>
                          <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                            {t("estudante.discount.monthly")}
                          </label>
                          <div className="icon-field">
                            <span className="icon">
                              <Icon icon="solar:ticket-sale-outline" />
                            </span>
                            <input
                              type='number'
                              className='form-control radius-8'
                              value={form.monthlyDiscount}
                              onChange={setField("monthlyDiscount")}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className='col-sm-4'>
                      <div className='mb-20'>
                        <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                          {t("estudante.discount.value")}
                        </label>
                        <div className="icon-field">
                          <span className="icon">
                            <Icon icon="solar:ticket-sale-outline" />
                          </span>
                          <input
                            type='number'
                            className='form-control radius-8'
                            value={form.discount}
                            onChange={setField("discount")}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className='col-12'>
                <button
                  type='button'
                  className='btn btn-outline-primary-600 btn-sm mb-20'
                  onClick={recalculate}
                >
                  {t("estudante.edit.recalculate")}
                </button>
                <div className='row gy-3'>
                  <div className='col-sm-4'>
                    <div className='p-16 radius-8 bg-neutral-50 text-center'>
                      <span className='text-secondary-light text-sm d-block'>
                        {t("estudante.summary.registrationValue")}
                      </span>
                      <strong className='text-primary-600'>{money(form.registrationValue)}</strong>
                    </div>
                  </div>
                  <div className='col-sm-4'>
                    <div className='p-16 radius-8 bg-neutral-50 text-center'>
                      <span className='text-secondary-light text-sm d-block'>
                        {t("estudante.summary.monthlyPayment")}
                      </span>
                      <strong className='text-primary-600'>{money(form.monthlyPayment)}</strong>
                    </div>
                  </div>
                  <div className='col-sm-4'>
                    <div className='p-16 radius-8 bg-neutral-50 text-center'>
                      <span className='text-secondary-light text-sm d-block'>
                        {t("estudante.summary.total")}
                      </span>
                      <strong className='text-success-600'>{money(form.total)}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>

            <div
              className='tab-pane fade'
              id='tab-documentos'
              role='tabpanel'
              aria-labelledby='tab-documentos-tab'
            >
            <div className='row'>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.docType")} <span className='text-danger-600'>*</span>
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.docType}
                    onChange={setField("docType")}
                  >
                    <option value=''>{t("estudante.select.placeholder")}</option>
                    <option value='BI'>{t("estudante.docType.bi")}</option>
                    <option value='Passaporte'>{t("estudante.docType.passport")}</option>
                    <option value='Cedula'>{t("estudante.docType.cedula")}</option>
                    <option value='Bolentim'>{t("estudante.docType.bolentim")}</option>
                    <option value='Nenhum'>{t("estudante.docType.none")}</option>
                  </select>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("estudante.field.docNumber")}
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:card-2-outline" />
                    </span>
                    <input
                      type='text'
                      className='form-control radius-8'
                      value={form.docNumber}
                      onChange={setField("docNumber")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-12'>
                <span className='text-secondary-light text-sm'>
                  {t("estudante.edit.tab.documents.photoHint")}
                </span>
              </div>
            </div>
            </div>
            </div>

            <div className='d-flex align-items-center justify-content-center gap-3 mt-16'>
              <button
                type='button'
                className='border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8'
                onClick={() => navigate("/estudantes/activos")}
                disabled={saving}
              >
                {t("global.cancel")}
              </button>
              <button
                type='button'
                className='btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8'
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "..." : <><i className='ri-save-line' /> {t("global.save")}</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditarEstudanteLayer;
